import React, { useState, useEffect, useContext } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Grid, CircularProgress, Box, Tab, Tabs } from '@mui/material';
import { withStyles } from '@mui/styles';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isAssignmentVisible } from '../../../utils/assignmentVisibilityHelpers';
import UpcomingAssignmentList from '../UpcomingAssignmentList/UpcomingAssignmentList';
import UpcomingAssignmentDetailView from '../UpcomingAssignmentDetailView/UpcomingAssignmentDetailView';
import UserContext from '../../../contexts/userContext';
import FacilityContacts from '../FacilityContacts/FacilityContacts';
import LocumTenensAfterHourSupportContact from '../LocumTenensContacts/LocumTenensAfterHourSupportContact';
import { getAssignments, getAssignmentTeam } from '../../../services/AssignmentsService';
import AssignmentDocumentComponentV2 from '../AssignmentDocumentComponent/AssignmentDocumentComponentV2';
import { FeatureFlag } from '../../GlobalComponents/FeatureFlagComponent/FeatureFlag';
import TravelV2 from '../Travel/TravelV2';
import TravelV3 from '../Travel/TravelV3';
import YourTeamGrid from '../YourTeam/YourTeamGrid';
import DayOneDetails from '../DayOneDetails/DayOneDetails';

const styles = () => ({
    contactsWidget: {
        marginTop: '20px',
    },
});

const UpcomingAssignmentsYourTeam = ({ setAssignmentPageError, classes }) => {
    const { externalId } = useContext(UserContext);
    const { ltoeWebAssignmentsPreBooking } = useFlags();
    const history = useHistory();
    const params = useParams();
    const urlId = params.id || null;

    const [isLoading, setIsLoading] = useState(true);
    const [assignments, setAssignments] = useState([]);
    const [assignmentTeam, setAssignmentTeam] = useState([]);
    const [isTeamLoading, setIsTeamLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const filterUpcomingAssignments = (rawAssignments) => {
        if (!rawAssignments) return null;
        return rawAssignments.filter(
            (assignment) =>
                assignment.location.addressLine1 ||
                (assignment.location.city && assignment.location.state)
        );
    };

    const getAssignmentBasedOnId = () => {
        if (!assignments) return null;
        const assignment = assignments.find((item) => item.id.toString() === urlId.toString());
        if (!isAssignmentVisible(assignment, ltoeWebAssignmentsPreBooking)) {
            return null;
        }
        return assignment;
    };

    const loadAssignmentTeam = async (assignment) => {
        if (!assignment) return;

        setIsTeamLoading(true);
        try {
            const team = await getAssignmentTeam(externalId, assignment.id, 'upcoming');
            setAssignmentTeam(team);
        } catch (error) {
            console.error('Error loading assignment team:', error);
        } finally {
            setIsTeamLoading(false);
        }
    };

    const updateUpcomingAssignments = async () => {
        setIsLoading(true);
        try {
            const rawAssignments = await getAssignments(externalId, 'upcoming');
            const filtered = filterUpcomingAssignments(rawAssignments);
            if (filtered !== null) {
                setAssignments(filtered);
            }
        } catch (e) {
            setAssignmentPageError(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const assignmentsData = await getAssignments(externalId, 'upcoming', true);
                const filtered = filterUpcomingAssignments(assignmentsData);
                setAssignments(filtered);
                setIsLoading(false);

                if (urlId && filtered) {
                    const assignment = filtered.find((a) => a.id.toString() === urlId.toString());
                    if (
                        assignment &&
                        isAssignmentVisible(assignment, ltoeWebAssignmentsPreBooking)
                    ) {
                        loadAssignmentTeam(assignment);
                    }
                }
            } catch (e) {
                setAssignmentPageError(e);
                setIsLoading(false);
            }
        };

        loadInitialData();
    }, [externalId, setAssignmentPageError, urlId, ltoeWebAssignmentsPreBooking]);

    const toggleAssignmentView = (assignmentId) => {
        history.push(`/assignments/upcoming/${assignmentId}`);
        setActiveTab(0);
    };

    const renderFacilityContact = (facilityId, contacts, primaryWorkLocation) => {
        if (!!facilityId && !!contacts && Array.isArray(contacts) && contacts.length > 0) {
            return (
                <FacilityContacts
                    facilityId={facilityId}
                    contacts={contacts}
                    primaryWorkLocation={primaryWorkLocation}
                    updateAssignments={updateUpcomingAssignments}
                />
            );
        }
        return null;
    };

    const renderDetailViewWidgets = () => {
        const currentAssignment = getAssignmentBasedOnId();
        if (!currentAssignment) return null;
        return (
            <div className={classes.contactsWidget}>
                <LocumTenensAfterHourSupportContact />
                {renderFacilityContact(
                    currentAssignment.facilityId,
                    currentAssignment.facilityContacts,
                    currentAssignment.displayName
                )}
            </div>
        );
    };

    const renderAssignmentView = () => {
        if (!urlId) {
            return (
                <UpcomingAssignmentList
                    assignments={assignments}
                    toggleAssignmentView={toggleAssignmentView}
                />
            );
        }
        const currentAssignment = getAssignmentBasedOnId();
        if (!currentAssignment) {
            return <div>Assignment not found or is not accessible.</div>;
        }

        const hasDayOneDetails = currentAssignment.areDayOneDetailsPresent;

        return (
            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                    <UpcomingAssignmentDetailView
                        assignment={currentAssignment}
                        externalId={externalId}
                        type="upcoming"
                        assignmentId={urlId}
                        assignmentTeam={assignmentTeam}
                    />

                    <Box sx={{ width: '100%', mt: 3 }}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            aria-label="Assignment Tabs"
                        >
                            {hasDayOneDetails && <Tab label="Day One" />}
                            <Tab label="Meet Your Team" />
                            <Tab label="Travel" />
                            <Tab label="Documents" />
                        </Tabs>

                        <Box>
                            {hasDayOneDetails && activeTab === 0 && (
                                <DayOneDetails
                                    assignment={currentAssignment}
                                    assignmentTeam={assignmentTeam}
                                />
                            )}

                            {activeTab === (hasDayOneDetails ? 1 : 0) &&
                                (isTeamLoading ? (
                                    <CircularProgress color="primary" />
                                ) : (
                                    <YourTeamGrid yourteam={assignmentTeam} />
                                ))}

                            {activeTab === (hasDayOneDetails ? 2 : 1) && (
                                <>
                                    <FeatureFlag
                                        name="TravelV3"
                                        isFlagEnabled="false"
                                        fallbackValue={false}
                                    >
                                        <TravelV2
                                            assignment={currentAssignment}
                                            externalId={externalId}
                                            filter="active"
                                        />
                                    </FeatureFlag>

                                    <FeatureFlag
                                        name="TravelV3"
                                        isFlagEnabled="true"
                                        fallbackValue={false}
                                    >
                                        <TravelV3
                                            assignment={currentAssignment}
                                            externalId={externalId}
                                            filter="active"
                                        />
                                    </FeatureFlag>
                                </>
                            )}

                            {activeTab === (hasDayOneDetails ? 3 : 2) && (
                                <AssignmentDocumentComponentV2
                                    assignmentName={currentAssignment.name}
                                />
                            )}
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={{ mt: -3 }}>{renderDetailViewWidgets()}</Box>
                </Grid>
            </Grid>
        );
    };

    if (isLoading) {
        return (
            <div>
                <CircularProgress color="primary" />
            </div>
        );
    }

    return <>{renderAssignmentView()}</>;
};

UpcomingAssignmentsYourTeam.propTypes = {
    setAssignmentPageError: PropTypes.func.isRequired,
    classes: PropTypes.shape({}).isRequired,
};

export default withStyles(styles)(UpcomingAssignmentsYourTeam);
